import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api, { ConnectedApp } from '@/lib/api';
import { OAuthScopeList } from './OAuthScopeList';

export function ConnectedAppsCard() {
  const [apps, setApps] = useState<ConnectedApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .getConnectedApps()
      .then((result) => setApps(result.apps))
      .catch((err) => setError(err.message || 'Failed to load connected apps'))
      .finally(() => setLoading(false));
  }, []);

  async function handleRevoke(appId: string) {
    setRevoking(appId);
    setError('');
    try {
      await api.revokeConnectedApp(appId);
      setApps((prev) => prev.filter((a) => a.appId !== appId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke access');
    } finally {
      setRevoking(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Connected Apps</CardTitle>
        <CardDescription>
          Third-party apps you have authorized to access your account
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading && <p className="text-sm text-muted-foreground">Loading...</p>}

        {error && <p className="text-sm text-destructive mb-3">{error}</p>}

        {!loading && apps.length === 0 && (
          <p className="text-sm text-muted-foreground">No connected apps</p>
        )}

        {apps.length > 0 && (
          <div className="space-y-4">
            {apps.map((app) => (
              <div
                key={app.appId}
                className="flex items-start justify-between gap-4 rounded-lg border p-3"
              >
                <div className="flex items-start gap-3 min-w-0">
                  {app.appLogoUrl && (
                    <img
                      src={app.appLogoUrl}
                      alt={app.appName}
                      className="h-8 w-8 rounded shrink-0"
                    />
                  )}
                  <div className="min-w-0">
                    <p className="font-medium text-sm">{app.appName}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Connected {new Date(app.grantedAt).toLocaleDateString()}
                    </p>
                    <div className="mt-2">
                      <OAuthScopeList scopes={app.scopes} />
                    </div>
                  </div>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={revoking === app.appId}
                  onClick={() => handleRevoke(app.appId)}
                >
                  {revoking === app.appId ? 'Revoking...' : 'Revoke'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
